import { OSP_TRACKING_EVENTS, OspTrackingProps, trackOspEvent } from '~/utils/osp-tracking'
import { getCameraDiagnostics } from '~/utils/camera-diagnostics'

export type OspCameraPayload = Record<string, unknown>

export interface OspCameraTrackingEvent {
  name: string,
  props: OspTrackingProps,
}

const OSP_CAMERA_EVENT = 'osp_camera'

function asString (value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function asNumber (value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

function resolveAction (payload: OspCameraPayload): string | null {
  const eventAction = asString(payload.eventAction)
  if (!eventAction) {
    return null
  }

  const separatorIndex = eventAction.indexOf('_')

  return separatorIndex === -1 ? eventAction : eventAction.slice(separatorIndex + 1)
}

export function mapOspCameraPayload (payload: unknown): OspCameraTrackingEvent | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const data = payload as OspCameraPayload
  if (data.event !== OSP_CAMERA_EVENT || data.camera_stage === 'assignment') {
    return null
  }

  const action = resolveAction(data)
  const props: OspTrackingProps = {
    purpose: asString(data.camera_purpose),
    documentSide: asString(data.camera_document_side),
    engine: asString(data.camera_engine),
    assignmentSource: asString(data.camera_assignment_source),
  }

  switch (action) {
    case 'request':
      return { name: OSP_TRACKING_EVENTS.OSP2_CAMERA_STARTED, props }
    case 'ready':
      return {
        name: OSP_TRACKING_EVENTS.OSP2_CAMERA_READY,
        props: { ...props, durationMs: asNumber(data.camera_duration_ms) },
      }
    case 'error':
      return {
        name: OSP_TRACKING_EVENTS.OSP2_VERIFICATION_ERROR,
        props: {
          ...props,
          kind: asString(data.camera_error_kind) ?? 'generic',
          stage: asString(data.camera_stage),
          errorName: asString(data.camera_error_name),
          permissionState: asString(data.camera_permission_state),
        },
      }
    case 'upload-error':
      return {
        name: OSP_TRACKING_EVENTS.OSP2_VERIFICATION_ERROR,
        props: {
          ...props,
          kind: 'upload',
          stage: asString(data.camera_stage) ?? 'upload',
          errorName: asString(data.camera_error_name),
          permissionState: null,
        },
      }
    default:
      return null
  }
}

export async function trackOspCameraPayload (payload: unknown): Promise<void> {
  const event = mapOspCameraPayload(payload)
  if (!event) {
    return
  }

  if (event.name === OSP_TRACKING_EVENTS.OSP2_VERIFICATION_ERROR) {
    const diagnostics = await getCameraDiagnostics()
    trackOspEvent(event.name, { ...event.props, ...diagnostics })
    return
  }

  trackOspEvent(event.name, event.props)
}
